import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import { v4 as uuidv4 } from "uuid";
import { Photo } from "@capacitor/camera";
import { InspectionInfo } from "../models/workorders/Inspection";
import {
  AnalysisCode,
  WorkOrderEvents,
} from "../models/analysiscodes/AnalysisCodes";
import { ProcessWorkOrderActionListData } from "../api/MobileApproval";
import { MaterialIssueBody, ReturnMaterialsBody } from "../api/Materials";

export const ADDING_COMMENT = "Adding Comment";
export const UPLOADING_FILE = "Uploading File";
export const UPLOADING_PHOTO = "Uploading Photo";
export const ADDING_METER_READING = "Adding Meter Reading";
export const COMPLETING_INSPECTION = "Completing Inspection";
export const CREATE_WO = "Creating Work Order";
export const ADDING_UPDATING_ANALYSIS_CODES = "Adding/Updating Analysis Code";
export const ADDING_TIME_ENTRY = "Adding New Entry";
export const UPDATE_STATUS = "Updating Work Order Status";
export const COMPLETING_WO = "Completing Work Order";
export const RETURN_MATERIAL = "Return Material";
export const ISSUE_MATERIAL = "Issue Material";

export type StatusType = "pending" | "uploading" | "success" | "failed";

export type OperationDetails = {
  status: StatusType;
  operation: string;
  id: string;
  canUpdated?: boolean;
  error?: string[];
  createdDate: Date;
  woNumber?: string;
  followUpToWoNumber?: string;
  parentOperationId?: string; //if its a follow up operation, e.g. create follow-up wo during completion
};

type CommentInfo = {
  comment: string;
  woNumber: string;
};

type FileData = {
  WoNumber: string;
  File: File;
  FileName: string;
};
type PhotoData = {
  WoNumber: string;
  Photo: Photo;
  FileName: string;
};

type NewMeterReading = {
  data: any;
  isUpdate: boolean;
  woNumber: string;
};

type NewWorkOrder = {
  confirmation: any;
  requestObject: any;
  followupWoNumber?: string;
};

type Payload = {
  RequestObject: any;
  Confirmation: any;
  woNumber: string;
};

type AnalysisCodeData = {
  codes: AnalysisCode[];
  workOrderEvents: WorkOrderEvents;
  woNumber: string;
};

type IssueMaterial = { woNumber: string } & MaterialIssueBody;
type ReturnMaterial = { woNumber: string } & ReturnMaterialsBody;
type WoStatusChange = { woNumber: string } & ProcessWorkOrderActionListData;

type Comment = OperationDetails & CommentInfo;
type FileOptions = OperationDetails & FileData;
type PhotoOptions = OperationDetails & PhotoData;
type MeterReading = OperationDetails & NewMeterReading;
type Inspection = OperationDetails & InspectionInfo;
type WorkOrder = OperationDetails & NewWorkOrder;
type AnalysisCodeInfo = OperationDetails & AnalysisCodeData;
export type TimeEntry = OperationDetails & Payload;
type Status = OperationDetails & WoStatusChange;
type WoToComplete = OperationDetails & Payload;
type Issue = OperationDetails & IssueMaterial;
type Return = OperationDetails & ReturnMaterial;

type WoOfflineUpdatesState = {
  comments: Comment[];
  fileToUpload: FileOptions[];
  photosToUpload: PhotoOptions[];
  newMeterReadings: MeterReading[];
  inspectionToBeCompleted: Inspection[];
  operationDetails: OperationDetails[];
  workOrdersToCreate: WorkOrder[];
  analysisCodeToAddUpdate: AnalysisCodeInfo[];
  timeEntryToAdd: TimeEntry[];
  statusToUpdate: Status[];
  woToComplete: WoToComplete[];
  issues: Issue[];
  returns: Return[];
};

type WoOfflineUpdatesAction = {
  addOfflineComments: (data: CommentInfo) => void;
  getCommentById: (id: string) => Comment | undefined;
  addOfflineFileToUpload: (file: FileData) => void;
  getFileToUploadById: (id: string) => FileOptions | undefined;
  addOfflinePhotoToUpload: (photo: PhotoData) => void;
  getPhotoToUploadById: (id: string) => PhotoOptions | undefined;
  addNewMeterReadings: (reading: NewMeterReading) => void;
  getMeterReadingById: (id: string) => MeterReading | undefined;
  addInspectionToBeCompleted: (inspection: InspectionInfo) => void;
  getInspectionById: (id: string) => Inspection | undefined;
  addWorkOrderToCreate: (workOrder: NewWorkOrder) => void;
  getWoToCreateById: (id: string) => WorkOrder | undefined;
  addAnalysisCodesToCreateUpdate: (analysisCode: AnalysisCodeData) => void;
  getAnalysisCodeById: (id: string) => AnalysisCodeInfo | undefined;
  addNewTimeEntry: (entry: Payload) => void;
  getTimeEntryById: (id: string) => TimeEntry | undefined;
  addStatusToUpdate: (status: WoStatusChange) => void;
  getStatusById: (id: string) => Status | undefined;
  addWoToComplete: (wo: Payload) => void;
  getWoToCompleteById: (id: string) => WoToComplete | undefined;
  addIssue: (issue: IssueMaterial) => void;
  getIssueById: (id: string) => Issue | undefined;
  addReturn: (returnInfo: ReturnMaterial) => void;
  getReturnById: (id: string) => Return | undefined;

  getWorkOrderById: (id: string) => WorkOrder | undefined;

  makeOperationCompletedById: (id: string, woNumber?: string) => void;
  updateOperationWoNumberById: (id: string, woNumber: string) => void;
  updateStatusById: (id: string, status: StatusType, error?: string[]) => void;
  updateById: (id: string, data: any) => void;
  deleteById: (id: string, keepDeps?: boolean) => void;
  deleteAllSuccessFullOps: () => void;
  hasFailedOperationBefore: (id: string) => boolean;
  hasInspectionAddedForCompletionInOffline: (
    inspectionId: string,
    woNumber: string
  ) => boolean;
};

function getPendingOperationDetails(
  operation: string,
  woNumber?: string,
  canUpdated?: boolean,
  parentOperationId?: string,
  followUpToWoNumber?: string
): OperationDetails {
  return {
    id: uuidv4(),
    operation,
    status: "pending",
    createdDate: new Date(),
    canUpdated,
    woNumber,
    parentOperationId,
    followUpToWoNumber,
  };
}

const defaultState: WoOfflineUpdatesState = {
  comments: [],
  fileToUpload: [],
  photosToUpload: [],
  newMeterReadings: [],
  inspectionToBeCompleted: [],
  operationDetails: [],
  workOrdersToCreate: [],
  analysisCodeToAddUpdate: [],
  timeEntryToAdd: [],
  statusToUpdate: [],
  woToComplete: [],
  issues: [],
  returns: [],
};

export const useWoOfflineUpdatesStore = create<
  WoOfflineUpdatesState & WoOfflineUpdatesAction
>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        addOfflineComments(data) {
          set((state) => {
            const operation = getPendingOperationDetails(
              ADDING_COMMENT,
              data.woNumber
            );
            state.comments = [
              ...state.comments,
              {
                ...data,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getCommentById(id) {
          return get().comments.find((c) => c.id === id);
        },

        addAnalysisCodesToCreateUpdate(analysisCode) {
          const operation = getPendingOperationDetails(
            ADDING_UPDATING_ANALYSIS_CODES,
            analysisCode.woNumber
          );
          set((state) => {
            state.analysisCodeToAddUpdate = [
              ...state.analysisCodeToAddUpdate,
              {
                ...analysisCode,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getAnalysisCodeById(id) {
          return get().analysisCodeToAddUpdate.find((a) => a.id === id);
        },

        addIssue(issue) {
          const operation = getPendingOperationDetails(
            ISSUE_MATERIAL,
            issue.woNumber,
            true
          );
          set((state) => {
            state.issues = [
              ...state.issues,
              {
                ...issue,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getIssueById(id) {
          return get().issues.find((a) => a.id === id);
        },

        addReturn(returnInfo) {
          const operation = getPendingOperationDetails(
            RETURN_MATERIAL,
            returnInfo.woNumber
          );
          set((state) => {
            state.returns = [
              ...state.returns,
              {
                ...returnInfo,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getReturnById(id) {
          return get().returns.find((a) => a.id === id);
        },

        addStatusToUpdate(status) {
          const operation = getPendingOperationDetails(
            UPDATE_STATUS,
            status.woNumber
          );
          set((state) => {
            state.statusToUpdate = [
              ...state.statusToUpdate,
              {
                ...status,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getStatusById(id) {
          return get().statusToUpdate.find((s) => s.id === id);
        },

        addNewTimeEntry(entry) {
          const operation = getPendingOperationDetails(
            ADDING_TIME_ENTRY,
            entry.woNumber,
            true
          );
          set((state) => {
            state.timeEntryToAdd = [
              ...state.timeEntryToAdd,
              {
                ...entry,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getTimeEntryById(id) {
          return get().timeEntryToAdd.find((t) => t.id === id);
        },

        addWoToComplete(wo) {
          console.log("adding complete work order for wo", wo.woNumber);

          const operation = getPendingOperationDetails(
            COMPLETING_WO,
            wo.woNumber,
            true
          );

          console.log("operation for wo to complete", operation);

          set((state) => {
            state.woToComplete = [
              ...state.woToComplete,
              {
                ...wo,
                ...operation,
              },
            ];
            console.log("final operation data: ", [
              ...state.operationDetails,
              operation,
            ]);

            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getWoToCompleteById(id) {
          return get().woToComplete.find((w) => w.id === id);
        },

        addOfflineFileToUpload(file) {
          const operation = getPendingOperationDetails(
            UPLOADING_FILE,
            file.WoNumber
          );
          set((state) => {
            state.fileToUpload = [
              ...state.fileToUpload,
              {
                ...file,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getFileToUploadById(id) {
          return get().fileToUpload.find((f) => f.id === id);
        },

        addWorkOrderToCreate(workOrder) {
          let parentOperationId: string | undefined = undefined;
          const { followupWoNumber } = workOrder;

          if (followupWoNumber) {
            parentOperationId = get().woToComplete.find(
              (w) => w.woNumber === followupWoNumber
            )?.id;
          }
          const operation = getPendingOperationDetails(
            CREATE_WO,
            undefined,
            true,
            parentOperationId,
            followupWoNumber
          );
          set((state) => {
            state.workOrdersToCreate = [
              ...state.workOrdersToCreate,
              {
                ...workOrder,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getWoToCreateById(id) {
          return get().workOrdersToCreate.find((w) => w.id === id);
        },

        addOfflinePhotoToUpload(photo) {
          const operation = getPendingOperationDetails(
            UPLOADING_PHOTO,
            photo.WoNumber
          );
          set((state) => {
            state.photosToUpload = [
              ...state.photosToUpload,
              {
                ...photo,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getPhotoToUploadById(id) {
          return get().photosToUpload.find((p) => p.id === id);
        },

        addNewMeterReadings(reading) {
          const operation = getPendingOperationDetails(
            ADDING_METER_READING,
            reading.woNumber
          );
          set((state) => {
            state.newMeterReadings = [
              ...state.newMeterReadings,
              {
                ...reading,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getMeterReadingById(id) {
          return get().newMeterReadings.find((m) => m.id === id);
        },

        addInspectionToBeCompleted(inspection) {
          const operation = getPendingOperationDetails(
            COMPLETING_INSPECTION,
            inspection.woNumber,
            true
          );
          set((state) => {
            state.inspectionToBeCompleted = [
              ...state.inspectionToBeCompleted,
              {
                ...inspection,
                ...operation,
              },
            ];
            state.operationDetails = [...state.operationDetails, operation];
          });
        },

        getInspectionById(id) {
          return get().inspectionToBeCompleted.find((i) => i.id === id);
        },

        updateOperationWoNumberById(id, woNumber) {
          set((state) => {
            state.operationDetails = state.operationDetails.map((op) => {
              if (op.id === id) {
                op.woNumber = woNumber;
              }
              return op;
            });
          });
        },

        makeOperationCompletedById(id, woNumber) {
          set((state) => {
            state.operationDetails = state.operationDetails.map((o) => {
              if (o.id === id)
                return {
                  ...o,
                  id: uuidv4(),
                  status: "success",
                  error: undefined,
                  woNumber: woNumber ?? o.woNumber,
                };
              return o;
            });
          });
          //remove from the storage
          get().deleteById(id, true);
        },

        updateStatusById(id, status, error) {
          set((state) => {
            for (const key in state) {
              const value = state[key as keyof WoOfflineUpdatesState];

              if (Array.isArray(value)) {
                const index = value.findIndex((item: any) => item.id === id);
                if (index !== -1) {
                  Object.assign(value[index], { status, error }); // directly mutate the draft
                }
              }
            }
          });
        },
        updateById(id, data) {
          set((state) => {
            for (const key in state) {
              const value = state[key as keyof WoOfflineUpdatesState];

              if (Array.isArray(value)) {
                const index = value.findIndex((item: any) => item.id === id);
                if (index !== -1) {
                  Object.assign(value[index], data); // directly mutate the draft
                }
              }
            }
          });
        },
        deleteById(id, keepDeps = false) {
          const state = get(); // get a snapshot first
          for (const key in state) {
            const value = state[key as keyof WoOfflineUpdatesState];
            if (Array.isArray(value)) {
              set((state) => {
                const currentArray = state[
                  key as keyof WoOfflineUpdatesState
                ] as OperationDetails[];
                const updatedArray = currentArray.filter((item) => {
                  if (keepDeps) {
                    // Keep items unless their id is different
                    return item.id !== id;
                  } else {
                    // Remove items matching either id or parentOperationId
                    return item.id !== id && item.parentOperationId !== id;
                  }
                });

                (state[
                  key as keyof WoOfflineUpdatesState
                ] as OperationDetails[]) = updatedArray;
              });
            }
          }
        },
        deleteAllSuccessFullOps() {
          set((state) => {
            state.operationDetails = state.operationDetails.filter(
              (o) => o.status !== "success"
            );
          });
        },
        getWorkOrderById(id) {
          return get().workOrdersToCreate.find((w) => w.id === id);
        },
        hasFailedOperationBefore(id) {
          const { operationDetails } = get();
          const ind = operationDetails.findIndex((o) => o.id === id);
          if (ind === -1) return false;
          return operationDetails
            .slice(0, ind)
            .some((o) => o.status === "failed");
        },

        hasInspectionAddedForCompletionInOffline(inspectionId, woNumber) {
          return get().inspectionToBeCompleted.some(
            (i) => i.inspectionId === inspectionId && i.woNumber === woNumber
          );
        },
      })),
      {
        name: "wo-order-offline-updates",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          // ✅ only persist these
          comments: state.comments,
          fileToUpload: state.fileToUpload,
          photosToUpload: state.photosToUpload,
          newMeterReadings: state.newMeterReadings,
          inspectionToBeCompleted: state.inspectionToBeCompleted,
          operationDetails: state.operationDetails,
          workOrdersToCreate: state.workOrdersToCreate,
          analysisCodeToAddUpdate: state.analysisCodeToAddUpdate,
          timeEntryToAdd: state.timeEntryToAdd,
          statusToUpdate: state.statusToUpdate,
          woToComplete: state.woToComplete,
          issues: state.issues,
          returns: state.returns,
        }),
      }
    ),
    {
      name: "WoOfflineUpdatesStore",
      enabled: true,
    }
  )
);
